import {Track} from '@app/web-player/tracks/track';

/**
 * Représente un Track enrichi de propriétés utiles pour l'affichage UI ou le filtrage.
 */
export interface EnhancedTrack extends Track {
  isReleased: boolean;
  isAvailable: boolean;
  isEncoded: boolean;
  isExclusivity: boolean;
}

/**
 * Décrit les permissions d'accès en visualisation pour un utilisateur donné.
 * Chaque propriété indique si l'utilisateur peut voir les contenus même s'ils ne sont pas disponibles publiquement.
 */
export interface MediaViewAccessPermissions {
  unreleased: boolean;
  unavailable: boolean;
  unencoded: boolean;
}

/**
 * Décrit les permissions d'accès à la lecture (play) d'un média.
 * Utilisé pour contrôler si un utilisateur peut écouter un track non publié, non disponible ou non encodé.
 */
export interface MediaPlayAccessPermissions {
  unreleased: boolean;
  unavailable: boolean;
  unencoded: boolean;
}

/**
 * Options de filtrage pour la visualisation d'un Track.
 * Permet d'activer ou non les critères (released, available, encoded) à appliquer,
 * tout en tenant compte des permissions d'accès correspondantes.
 */
export interface TrackViewAccessOptions {
  permissions: MediaViewAccessPermissions;
  userId?: number;
  useReleased?: boolean;
  useAvailable?: boolean;
  useEncoded?: boolean;
}

/**
 * Options d'évaluation pour la lecture d'un Track.
 * Fonctionne comme les options de vue, mais pour la logique de lecture.
 */
export interface TrackPlayAccessOptions {
  permissions: MediaPlayAccessPermissions;
  userId?: number;
  useReleased?: boolean;
  useAvailable?: boolean;
  useEncoded?: boolean;
}

/**
 * Crée une fonction de filtre à appliquer sur un tableau de tracks,
 * qui détermine leur accessibilité en fonction des permissions et des règles activées.
 */
export function createTrackViewFilter(options: TrackViewAccessOptions) {
  const {
    userId,
    useReleased = false,
    useAvailable = true,
    useEncoded = true
  } = options;
  const permissions = normalizePermissions(options.permissions, {
      unreleased: false,
      unavailable: false,
      unencoded: false
  });
  const now = new Date();

  return (track: Track): boolean => {
    if (userId && track.owner_id === userId) return true;

    const releaseDate = track.k_release_date ?? track.release_date;
    const isReleased = releaseDate ? new Date(releaseDate) <= now : false;
    const isAvailable = 'available' in track ? !!track.available : true;
    const isEncoded = 'encoded' in track ? !!track.encoded : true;
    
    return (
      (!useReleased || permissions.unreleased || isReleased) &&
      (!useAvailable || permissions.unavailable || isAvailable) &&
      (!useEncoded || permissions.unencoded || isEncoded)
    );
  };
}

/**
 * Crée une fonction de filtre à appliquer sur un tableau de tracks,
 * pour déterminer s’ils sont lisibles selon les permissions utilisateur.
 */
export function createTrackPlayFilter(options: TrackPlayAccessOptions) {
  const {
    userId,
    useReleased = true,
    useAvailable = true,
    useEncoded = true
  } = options;
  const permissions = normalizePermissions(options.permissions, {
      unreleased: false,
      unavailable: false,
      unencoded: false
  });

  const now = new Date();

  return (track: Track): boolean => {
    if (userId && track.owner_id === userId) return true;

    const releaseDate = track.k_release_date ?? track.release_date;
    const isReleased = releaseDate ? new Date(releaseDate) <= now : false;
    const isAvailable = 'available' in track ? !!track.available : true;
    const isEncoded = 'encoded' in track ? !!track.encoded : true;

    return (
      (!useReleased || permissions.unreleased || isReleased) &&
      (!useAvailable || permissions.unavailable || isAvailable) &&
      (!useEncoded || permissions.unencoded || isEncoded)
    );
  };
}

/**
 * Vérifie si un track est visible (dans l'interface) selon les options et les permissions d'accès.
 */
export function canViewTrack(track: Track, options: TrackViewAccessOptions): boolean {
  if (options.userId && track.owner_id === options.userId) return true;

  const {
    useReleased = false,
    useAvailable = true,
    useEncoded = true
  } = options;
  const permissions = normalizePermissions(options.permissions, {
      unreleased: false,
      unavailable: false,
      unencoded: false
  });
  const now = new Date();
  const releaseDate = track.k_release_date ?? track.release_date;
  const isReleased = releaseDate ? new Date(releaseDate) <= now : false;
  const isAvailable = 'available' in track ? !!track.available : true;
  const isEncoded = 'encoded' in track ? !!track.encoded : true;

  return (
    (!useReleased || permissions.unreleased || isReleased) &&
    (!useAvailable || permissions.unavailable || isAvailable) &&
    (!useEncoded || permissions.unencoded || isEncoded)
  );
}

/**
 * Vérifie si un utilisateur a le droit de lire (jouer) un track,
 * selon ses permissions et les règles métiers activées.
 */
export function canPlayTrack(track: Track, options: TrackPlayAccessOptions): boolean {
  if (options.userId && track.owner_id === options.userId) return true;

  const {
    useReleased = true,
    useAvailable = true,
    useEncoded = true
  } = options;
  const permissions = normalizePermissions(options.permissions, {
      unreleased: false,
      unavailable: false,
      unencoded: false
  });
  
  const now = new Date();
  const releaseDate = track.k_release_date ?? track.release_date;
  const isReleased = releaseDate ? new Date(releaseDate) <= now : false;
  const isAvailable = 'available' in track ? !!track.available : true;
  const isEncoded = 'encoded' in track ? !!track.encoded : true;

  return (
    (!useReleased || permissions.unreleased || isReleased) &&
    (!useAvailable || permissions.unavailable || isAvailable) &&
    (!useEncoded || permissions.unencoded || isEncoded)
  );
}

/**
 * Enrichit un track avec des propriétés calculées utiles à l'affichage ou au filtrage.
 * Ces flags sont : isReleased, isAvailable, isEncoded, isExclusivity.
 */
export function addTrackFlags<T extends Track>(track: T): T & EnhancedTrack {
  const now = new Date();

  const kDate = track.k_release_date ? new Date(track.k_release_date) : undefined;
  const publicDate = track.release_date ? new Date(track.release_date) : undefined;

  const releaseDate = kDate ?? publicDate;
  const isReleased = releaseDate ? releaseDate <= now : false;
  const isAvailable = 'available' in track ? !!track.available : true;
  const isEncoded = 'encoded' in track ? !!track.encoded : true;

  const isExclusivity =
    track.album?.exclusivity ||
    kDate && publicDate && kDate < publicDate && now < publicDate;

  return {
    ...track,
    isReleased,
    isAvailable,
    isEncoded,
    isExclusivity: !!isExclusivity,
    releaseDate,
  };
}

function normalizePermissions<T extends Record<string, any>>(
  source: Partial<T> | undefined,
  defaults: T
): T {
  return {
    ...defaults,
    ...Object.fromEntries(
      Object.entries(defaults).map(([key, def]) => [key, source?.[key] ?? def])
    ),
  };
}
